import {
  fetchSchoolDistrictGetSchool,
  fetchSchoolDistrictGetSchoolDistrict,
  schoolDistrictGetSchoolDistrictQuery,
  schoolDistrictGetSchoolQuery,
} from "@crowdcoursing/api/openapi";
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

import { type Organization } from "~ui/organisms/Organizations/types";

function getDetailQuery(org: Organization) {
  if (!org.bubbleId || !org.id) {
    return null;
  }

  if (org.organizationType === "SchoolDistrict") {
    const variables = {
      pathParams: { bubbleId: org.bubbleId, schoolDistrictId: org.id },
    };
    return {
      queryFn: () => fetchSchoolDistrictGetSchoolDistrict(variables),
      queryKey: schoolDistrictGetSchoolDistrictQuery(variables).queryKey,
    };
  }

  if (org.organizationType === "School") {
    const variables = {
      pathParams: { bubbleId: org.bubbleId, schoolId: org.id },
    };
    return {
      queryFn: () => fetchSchoolDistrictGetSchool(variables),
      queryKey: schoolDistrictGetSchoolQuery(variables).queryKey,
    };
  }

  return null;
}

export function useMyOrganizationCardDetails(organizations: Organization[]): Organization[] {
  const detailQueries = useQueries({
    queries: organizations.map((organization) => {
      const detailQuery = getDetailQuery(organization);
      if (!detailQuery) {
        return {
          enabled: false,
          queryFn: async () => null,
          queryKey: ["myOrganizationCardDetails", "skipped", organization.id],
        };
      }

      return {
        ...detailQuery,
        enabled: true,
        retry: false,
      };
    }),
  });

  return useMemo(
    () =>
      organizations.map((organization, index) => {
        const details = detailQueries[index]?.data;
        if (!details) {
          return organization;
        }

        return {
          ...organization,
          phoneNumber: details.phoneNumber ?? organization.phoneNumber,
          slug: details.slug ?? organization.slug,
        };
      }),
    [detailQueries, organizations],
  );
}
